import type { SortingState } from '@tanstack/react-table';
import { Actions } from '../../components/product-table/columns/actions-column';
import { AvgSalesInterval } from '../../components/product-table/columns/avg-sales-interval-column';
import { DaysOverdue } from '../../components/product-table/columns/days-overdue-column';
import { ExtendedCost } from '../../components/product-table/columns/extended-cost-column';
import { ItemCode } from '../../components/product-table/columns/item-code-column';
import { LastSold } from '../../components/product-table/columns/last-sold-column';
import { ProductNameWithLabels } from '../../components/product-table/columns/product-name-with-labels-column';
import { QOH } from '../../components/product-table/columns/qoh-column';
import { Size } from '../../components/product-table/columns/size-column';
import { Type } from '../../components/product-table/columns/type-column';
import { VendorName } from '../../components/product-table/columns/vendor-name-column';
import { DepartmentFilter } from '../../components/product-table/filters/department-filter';
import { FiltersPopover } from '../../components/product-table/filters/filters-popover';
import { HideDisabledFilter } from '../../components/product-table/filters/hide-disabled-filter';
import { TotalValue } from '../../components/product-table/filters/total-value-filter';
import type { StrictColumnDef } from '../../components/tanstack-table/column';
import { ReportActions } from '../../components/tanstack-table/report-actions';
import { TanStackTable } from '../../components/tanstack-table/table';
import { useRouteRowSelection } from '../../components/tanstack-table/use-route-row-selection';
import { useTableRef } from '../../components/tanstack-table/use-table-ref';
import { PageToolbar } from '../../components/toolbar/page-toolbar';
import { SearchWithCount } from '../../components/toolbar/search-with-count';
import { ProductTableContext } from '../../contexts/product-table-context';
import type { Product } from '../../data/product';
import { type Filters, useFilteredProducts } from '../../hooks/products';
import { productReportsLayout } from '../../router';

const columns: StrictColumnDef<Product>[] = [
  VendorName,
  ItemCode,
  ProductNameWithLabels,
  Type,
  Size,
  LastSold,
  AvgSalesInterval,
  DaysOverdue,
  QOH,
  ExtendedCost,
  Actions,
];

const sorting: SortingState = [{ id: DaysOverdue.id, desc: true }];

const defaultFilters: Filters = {
  minimumInventory: 1,
  hideDisabled: true,
  stockingIssue: true,
};

export function StockingIssuesReport() {
  const products = productReportsLayout.useLoaderData();
  const { filters, setFilters, filteredProducts, baselineCount } = useFilteredProducts(
    products,
    defaultFilters
  );
  const { rowSelection, onRowSelectionChange, onRowClick } = useRouteRowSelection();
  const [table, onTableReady] = useTableRef<Product>();

  return (
    <>
      <ProductTableContext.Provider value={{ filters, setFilters, filteredProducts }}>
        <PageToolbar
          left={
            <FiltersPopover defaultFilters={defaultFilters}>
              <HideDisabledFilter />
              <DepartmentFilter products={products} />
            </FiltersPopover>
          }
          right={
            <>
              <TotalValue />
              <SearchWithCount
                quantity={filteredProducts.length}
                total={baselineCount}
                noun="products"
              />
              <ReportActions table={table} title="Possible Stocking Issues" />
            </>
          }
        />
      </ProductTableContext.Provider>
      <TanStackTable
        data={filteredProducts}
        columns={columns}
        sorting={sorting}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        onRowClick={onRowClick}
        getRowId={(product) => product.id}
        onTableReady={onTableReady}
      />
    </>
  );
}
